import { useState, useEffect } from "react";
import { T, S, fmtDate } from "./theme";
import { Badge, Btn } from "./components";
import { fetchMonthlyReport, fetchWeeklyReport, exportMonthlyCSV, exportWeeklyCSV, fetchVISNs, fetchStations, fetchSpecialties, fetchWeeks } from "./api";

const MONTH_NAMES = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTH_OPTIONS = MONTH_NAMES.slice(1).map((name, index) => ({ value: String(index + 1), label: name }));
const YEAR_OPTIONS = ["2026", "2025"];

export default function Reports({ showToast }) {
  const [reportSub, setReportSub] = useState("monthly");

  /* ── Filters ── */
  const [visns, setVisns] = useState([]);
  const [stationOpts, setStationOpts] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [weeks, setWeeks] = useState([]);

  /* ── Monthly state ── */
  const [monthlyYear, setMonthlyYear] = useState(YEAR_OPTIONS[0]);
  const [monthlyMonth, setMonthlyMonth] = useState(() => String(new Date().getMonth() + 1));
  const [monthlyVisn, setMonthlyVisn] = useState("");
  const [monthlyStation, setMonthlyStation] = useState("");
  const [monthlySpecialty, setMonthlySpecialty] = useState("");
  const [monthlySearch, setMonthlySearch] = useState("");
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyPagination, setMonthlyPagination] = useState(null);
  const [monthlyPage, setMonthlyPage] = useState(1);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  /* ── Weekly state ── */
  const [weeklyWeek, setWeeklyWeek] = useState("");
  const [weeklyVisn, setWeeklyVisn] = useState("");
  const [weeklySearch, setWeeklySearch] = useState("");
  const [weeklyData, setWeeklyData] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyPage, setWeeklyPage] = useState(1);
  const filterSelectStyle = { ...S.filterSelect, width: 150 };
  const stationSelectStyle = { ...S.filterSelect, width: 360, maxWidth: "100%" };

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, sRes, spRes] = await Promise.all([
          fetchVISNs(), fetchStations(), fetchSpecialties(),
        ]);
        setVisns(vRes.visns || []);
        setStationOpts(sRes.stations || []);
        setSpecialties(spRes.specialties || []);
      } catch (err) {
        console.error("Failed to load filter data:", err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (reportSub !== "weekly" || weeks.length > 0) return;
    const loadWeeks = async () => {
      try {
        const wRes = await fetchWeeks();
        const availableWeeks = wRes.weeks || [];
        setWeeks(availableWeeks);
        if (availableWeeks.length > 0) {
          setWeeklyWeek(availableWeeks[0]);
        }
      } catch (err) {
        console.error("Failed to load weekly filter data:", err);
      }
    };
    loadWeeks();
  }, [reportSub, weeks.length]);

  const generateMonthly = async () => {
    if (!monthlyYear || !monthlyMonth) return;
    setMonthlyLoading(true);
    try {
      const res = await fetchMonthlyReport({
        report_year: monthlyYear,
        report_month: monthlyMonth,
        visn: monthlyVisn || undefined,
        station_id: monthlyStation || undefined,
        specialty: monthlySpecialty || undefined,
        page: monthlyPage,
        limit: 50,
      });
      setMonthlyData(res.data || []);
      setMonthlyPagination(res.pagination || null);
    } catch (err) {
      console.error("Failed to fetch monthly report:", err);
      showToast("Failed to generate monthly report");
    } finally {
      setMonthlyLoading(false);
    }
  };

  const generateWeekly = async () => {
    if (!weeklyWeek) return;
    setWeeklyLoading(true);
    try {
      const res = await fetchWeeklyReport({
        report_week: weeklyWeek,
        visn: weeklyVisn || undefined,
      });
      setWeeklyData(res.data || []);
    } catch (err) {
      console.error("Failed to fetch weekly report:", err);
      showToast("Failed to generate weekly report");
    } finally {
      setWeeklyLoading(false);
    }
  };

  const formatMonth = (num) => MONTH_NAMES[parseInt(num)] || `Month ${num}`;
  const formatPeriod = (year, month) => year && month ? `${formatMonth(month)} ${year}` : "";
  const formatWeek = (value) => value ? fmtDate(value) : "";
  const formatYYYYMM = (value) => {
    const text = String(value || "");
    if (!/^\d{6}$/.test(text)) return "";
    return `${formatMonth(text.slice(4, 6))} ${text.slice(0, 4)}`;
  };
  const displaySecid = (value) => String(value || "").replace(/^(decommissioned|decommensioned)[, _-]*/i, "");

  return (
    <>
      <div style={S.subtabs}>
        {[
          { label: "Weekly Adoption" },
          { label: "Daily Rolling 2wk Raw" },
        ].map(({ label }) => (
          <button key={label} disabled style={{ ...S.subtab(false), opacity: 0.45, cursor: "not-allowed", display: "flex", alignItems: "center", gap: 6 }}>
            {label}
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: T.orange, background: T.orangeSoft, borderRadius: 4, padding: "1px 5px" }}>soon</span>
          </button>
        ))}
      </div>

      {/* Placeholder content */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", gap: 12 }}>
        <div style={{ fontSize: 32 }}>📊</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Reports coming soon</div>
        <div style={{ fontSize: 13, color: T.textMuted, textAlign: "center", maxWidth: 480, lineHeight: 2 }}>
          <div>Monthly report → License Tracker · <strong style={{ color: T.text }}>Total Licensed</strong> card</div>
          <div>Weekly report → License Tracker · <strong style={{ color: T.text }}>License to Launch</strong> card detail</div>
          <div style={{ marginTop: 4, color: T.textDim }}>Weekly adoption and daily rolling 2-week raw reports are in development.</div>
        </div>
      </div>

      {/* ── Monthly (hidden while disabled) ── */}
      {reportSub === "monthly" && false && (
        <>
          <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Generate Monthly Report</h2>
          <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>Select a year and month, then apply filters to generate a provider-level activation report.</p>
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <select style={filterSelectStyle} value={monthlyYear} onChange={(e) => setMonthlyYear(e.target.value)}>
              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select style={filterSelectStyle} value={monthlyMonth} onChange={(e) => setMonthlyMonth(e.target.value)}>
              {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select style={filterSelectStyle} value={monthlyVisn} onChange={(e) => setMonthlyVisn(e.target.value)}>
              <option value="">All VISNs</option>
              {visns.map((v) => <option key={v} value={v}>VISN {v}</option>)}
            </select>
            <select style={stationSelectStyle} value={monthlyStation} onChange={(e) => setMonthlyStation(e.target.value)}>
              <option value="">All Stations</option>
              {stationOpts.map((s) => <option key={s.station_id} value={s.station_id}>{s.station_id} — {s.site_name}</option>)}
            </select>
            <select style={filterSelectStyle} value={monthlySpecialty} onChange={(e) => setMonthlySpecialty(e.target.value)}>
              <option value="">All Specialties</option>
              {specialties.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
            </select>
            <input style={S.filterInput} placeholder="Search providers..." value={monthlySearch} onChange={(e) => setMonthlySearch(e.target.value)} />
            <Btn variant="primary" onClick={generateMonthly} style={{ opacity: monthlyLoading ? 0.6 : 1 }}>
              {monthlyLoading ? "Loading..." : "Generate Report"}
            </Btn>
          </div>
          {monthlyData ? (() => {
            const perPage = 25;
            const filteredMonthly = monthlyData.filter((p) => {
              if (!monthlySearch) return true;
              const q = monthlySearch.toLowerCase();
              return (p.provider_name || "").toLowerCase().includes(q) ||
                     (p.secid || "").toLowerCase().includes(q) ||
                     (p.email || "").toLowerCase().includes(q) ||
                     (p.site_name || "").toLowerCase().includes(q);
            });
            const totalPages = Math.max(1, Math.ceil(filteredMonthly.length / perPage));
            const page = Math.min(monthlyPage, totalPages);
            const paged = filteredMonthly.slice((page - 1) * perPage, page * perPage);
            return (
              <div style={S.tableWrap}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>Monthly Report — {formatPeriod(monthlyYear, monthlyMonth)}</span>
                    <span style={{ fontSize: 12, color: T.textMuted }}>Showing first 50 users; export CSV to see more</span>
                  </span>
                  <Btn sm variant="green" onClick={() => exportMonthlyCSV({ report_year: monthlyYear, report_month: monthlyMonth, visn: monthlyVisn, station_id: monthlyStation, specialty: monthlySpecialty }).then(() => showToast("CSV exported"))}>⬇ Export CSV</Btn>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["VISN", "Station ID", "Site Name", "Provider", "Email", "Specialty", "SECID", "Activated"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {paged.map((p) => (
                      <tr key={p.secid} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.surface2} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                        <td style={S.td}>{p.visn}</td><td style={{ ...S.td, ...S.mono }}>{p.station_id}</td><td style={S.td}>{p.site_name}</td><td style={S.td}>{p.provider_name}</td><td style={{ ...S.td, ...S.muted }}>{p.email}</td>
                        <td style={S.td}><Badge>{p.specialty}</Badge></td><td style={{ ...S.td, ...S.mono }}>{p.future_decommission_month ? <><span style={{ color: T.red, fontWeight: 600 }}>Decommissions {formatYYYYMM(p.future_decommission_month)}, </span>{displaySecid(p.secid)}</> : p.secid}</td><td style={S.td}>{fmtDate(p.date_activated)}</td>
                      </tr>
                    ))}
                    {paged.length === 0 && (
                      <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: T.textDim }}>No records found</td></tr>
                    )}
                  </tbody>
                </table>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 12, color: T.textMuted }}>Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filteredMonthly.length)} of {filteredMonthly.length} providers</span>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <Btn sm variant="ghost" onClick={() => setMonthlyPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                    <span style={{ fontSize: 12, color: T.textMuted, padding: "0 8px" }}>Page {page} of {totalPages}</span>
                    <Btn sm variant="ghost" onClick={() => setMonthlyPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                  </div>
                </div>
              </div>
            );
          })() : (
            <div style={{ textAlign: "center", padding: "60px 20px", color: T.textDim }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>📋</div>
              <div style={{ fontSize: 14 }}>Select a year and month, then click "Generate Report" to view provider activations.</div>
            </div>
          )}
        </>
      )}

      {/* ── Weekly ── */}
      {reportSub === "weekly" && (
        <>
          <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Weekly Site Utilization</h2>
          <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>Select a report week to view station-level utilization from the weekly site table.</p>
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
            <select style={{ ...S.filterSelect, width: 220 }} value={weeklyWeek} onChange={(e) => setWeeklyWeek(e.target.value)}>
              {weeks.map((w) => <option key={w} value={w}>Week of {formatWeek(w)}</option>)}
              {weeks.length === 0 && <option value="">No weeks available</option>}
            </select>
            <select style={filterSelectStyle} value={weeklyVisn} onChange={(e) => setWeeklyVisn(e.target.value)}>
              <option value="">All VISNs</option>
              {visns.map((v) => <option key={v} value={v}>VISN {v}</option>)}
            </select>
            <input style={S.filterInput} placeholder="Search stations..." value={weeklySearch} onChange={(e) => { setWeeklySearch(e.target.value); setWeeklyPage(1); }} />
            <Btn variant="primary" onClick={generateWeekly} style={{ opacity: weeklyLoading ? 0.6 : 1 }}>
              {weeklyLoading ? "Loading..." : "Generate Report"}
            </Btn>
          </div>
          {weeklyData ? (() => {
            const perPage = 25;
            const groupedWeekly = Object.values(
              weeklyData.reduce((acc, row) => {
                const rawStationId = String(row.station_id || "").trim();
                const baseStationId = rawStationId.slice(0, 3) || rawStationId;
                const reportWeek = row.report_week || weeklyWeek;
                const key = `${baseStationId}-${reportWeek}`;
                if (!baseStationId) return acc;

                if (!acc[key]) {
                  acc[key] = {
                    ...row,
                    station_id: baseStationId,
                    report_week: reportWeek,
                    station_ids: [],
                    site_names: [],
                    pcp_fte: 0,
                    activated: 0,
                    remaining_users: 0,
                  };
                }

                acc[key].station_ids.push(rawStationId);
                if (row.site_name) acc[key].site_names.push(row.site_name);
                acc[key].pcp_fte += Number(row.pcp_fte || 0);
                acc[key].activated += Number(row.activated || 0);
                acc[key].remaining_users += Number(row.remaining_users || 0);
                return acc;
              }, {})
            ).map((row) => {
              const uniqueStationIds = [...new Set(row.station_ids)].sort();
              const uniqueSiteNames = [...new Set(row.site_names)].sort();
              return {
                ...row,
                sites_included: uniqueStationIds.join(", "),
                subsite_count: uniqueStationIds.length,
                site_name: uniqueSiteNames.length <= 1 ? uniqueSiteNames[0] || "" : `${uniqueSiteNames.length} sites`,
              };
            });

            const filteredWeekly = groupedWeekly.filter((st) => {
              if (!weeklySearch) return true;
              const q = weeklySearch.toLowerCase();
              return (st.station_id || "").toLowerCase().includes(q) ||
                     (st.sites_included || "").toLowerCase().includes(q) ||
                     (st.site_name || "").toLowerCase().includes(q) ||
                     (st.city || "").toLowerCase().includes(q) ||
                     String(st.report_week || "").toLowerCase().includes(q);
            });
            const totalPages = Math.max(1, Math.ceil(filteredWeekly.length / perPage));
            const page = Math.min(weeklyPage, totalPages);
            const paged = filteredWeekly.slice((page - 1) * perPage, page * perPage);
            return (
            <div style={S.tableWrap}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Weekly Site Utilization — Week of {formatWeek(weeklyWeek)}</span>
                <Btn sm variant="green" onClick={() => exportWeeklyCSV({ report_week: weeklyWeek, visn: weeklyVisn }).then(() => showToast("CSV exported"))}>⬇ Export CSV</Btn>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["VISN", "Station", "Subsites", "Report Week", "PCP FTE", "Launched", "Remaining Users"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {paged.map((st) => (
                    <tr key={`${st.station_id}-${st.report_week}`} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.surface2} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                      <td style={S.td}>{st.visn}</td><td style={{ ...S.td, ...S.mono }}>{st.station_id}</td><td style={S.td}>{st.subsite_count}</td><td style={S.td}>{fmtDate(st.report_week)}</td>
                      <td style={S.td}>{st.pcp_fte ?? 0}</td><td style={S.td}>{st.activated ?? 0}</td><td style={S.td}>{st.remaining_users ?? 0}</td>
                    </tr>
                  ))}
                  {paged.length === 0 && (
                    <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: T.textDim }}>No weekly utilization found</td></tr>
                  )}
                </tbody>
              </table>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 12, color: T.textMuted }}>Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filteredWeekly.length)} of {filteredWeekly.length} station groups</span>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <Btn sm variant="ghost" onClick={() => setWeeklyPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                  <span style={{ fontSize: 12, color: T.textMuted, padding: "0 8px" }}>Page {page} of {totalPages}</span>
                  <Btn sm variant="ghost" onClick={() => setWeeklyPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                </div>
              </div>
            </div>
            );
          })() : (
            <div style={{ textAlign: "center", padding: "60px 20px", color: T.textDim }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>📊</div>
              <div style={{ fontSize: 14 }}>Select a report week and click "Generate Report" to view site utilization.</div>
            </div>
          )}
        </>
      )}
    </>
  );
}
